import { watch } from 'vue';
import { currentProduct } from '@/product';
import type { Locale } from './index';

/** The part of the auth store this needs — kept narrow so the rule is testable without pinia. */
export interface LanguageSyncTarget {
  readonly isAuthenticated: boolean;
  setPreferredLanguage(lang: Locale): void;
}

/**
 * One language for the cabinet and the server.
 *
 * The server renders everything the client gets outside the screen — the card activity statement, e-mails, pushes,
 * event lines — in {@code Customer.preferredLanguage}. That value must therefore be the language the cabinet shows.
 * It used to be pushed only when the language had been guessed from the browser at boot, while App.vue then forced
 * the screen to Russian: a Russian cabinet produced an English statement. Now, whenever a vitrine session starts
 * (boot restore, magic link, OTP login), the language on screen is what the server stores. Switching it in
 * Settings pushes it again (auth.setPreferredLanguage).
 */
export function keepServerLanguageInSync(target: LanguageSyncTarget, uiLanguage: () => Locale): void {
  watch(() => target.isAuthenticated, authed => {
    // The endpoint is the vitrine's (/showcase/me/language); the full product has no such setting server-side.
    if (authed && currentProduct() === 'vitrine') target.setPreferredLanguage(uiLanguage());
  }, { immediate: true });
}
